import React from 'react';
import { G, Path, Rect } from '@react-pdf/renderer';
import { mapValues } from 'lodash';

/**
 * Interface for door configuration
 */
interface DoorConfig {
  scale: number;
  angle: number;
  x: number;
  y: number;
}

/**
 * Interface for area data
 */
interface Area {
  door: DoorConfig;
  [key: string]: any;
}

/**
 * Interface for areas object
 */
interface Areas {
  [key: string]: Area;
}

/**
 * Interface for content
 */
interface Content {
  areas: Areas;
  [key: string]: any;
}

/**
 * Interface for door dimensions
 */
interface DoorDimensions {
  height: number;
  width: number;
  arch: number;
}

/**
 * Interface for component props
 */
interface MuiDoorProps {
  areaId: string;
  scale: number;
  content: Content;
}

/**
 * MUI Door component that replaces the styled-components Door component
 * 
 * This component provides a Material UI equivalent of the original Door component
 * with similar props and functionality. It's used for PDF generation.
 * 
 * @param {MuiDoorProps} props
 * @returns {JSX.Element} MUI Door component
 */
const MuiDoor: React.FC<MuiDoorProps> = (props) => {
  const { areaId, scale, content: { areas } } = props;
  const { door: { scale: doorScale, angle, x, y } } = areas[areaId];
  const door = mapValues(DoorSize, (v) => v * scale);

  return (
    <G transform={`translate(${(x * scale) + 1}, ${(y * scale) + 1}) rotate(${angle}) scale(${doorScale}) `} >
      <Rect x={0} y={0} width={door.width} height={door.height} stroke='#000' fill='#000' />
      <Path d={`M${door.width}, 0a${door.arch} ${door.arch} 0 0 1 ${door.arch} ${door.height}`} stroke='#000' fill='none' strokeWidth='1' {...{'shapeRendering': 'geometricprecision'} as any} />
    </G>
  );
};

/**
 * Door size constants in meters
 */
const DoorSize: DoorDimensions = {
  height: 0.9144,
  width: 0.03175,
  arch: 0.88265, // height - width
};

export default MuiDoor;
